<template>
  <div class="flex flex-col gap-2">
    <div class="flex text-sm leading-4">
      <transition
        enter-active-class="transition-all duration-300"
        enter-from-class="transform -translate-y-3 opacity-0"
        enter-to-class="transform translate-y-0 opacity-100"
        leave-active-class="transition-all duration-300"
        leave-from-class="transform translate-y-0"
        leave-to-class="transform -translate-y-3 opacity-0"
      >
        <span v-if="errorMessage" class="text-red-600 mt-1">
          <slot name="error">
            {{ errorMessage }}
          </slot>
        </span>
      </transition>
    </div>
    <div v-if="isDebug" class="text-red-600">
      reCaptcha Dev Mode On. Please double check the form submission
      functionality on production.
    </div>
    <i18n-t
      keypath="recaptcha.policies_text"
      tag="div"
      class="text-neutral-500 text-sm"
    >
      <template #privacy_policy>
        <BaseLink href="https://policies.google.com/privacy" target="_blank">
          {{ t('recaptcha.privacy_policy') }}
        </BaseLink>
      </template>
      <template #terms_of_service>
        <BaseLink href="https://policies.google.com/terms" target="_blank">
          {{ t('recaptcha.terms_of_service') }}
        </BaseLink>
      </template>
    </i18n-t>
    {{ value }}
  </div>
</template>

<script setup lang="ts">
import { useField, useFormErrors } from 'vee-validate'
import { secondsToMilliseconds } from '~core/plugins/helpers/time-conversion'
import { RecaptchaReset } from '~/composables/use-recaptcha'

interface Props {
  name?: string
}

const props = withDefaults(defineProps<Props>(), {
  name: 'recaptcha',
})

const emits = defineEmits<{
  /**
   * Recaptcha does not send an event that the user has canceled the check.
   * In order not to permanently block the form submission,
   * we allow the user to re-submit the form until the validation is complete.
   * But we block the submission for a short time to avoid too frequent checks.
   * We pass the state of this blocking to the parent component to visually lock the submit button
   * and switch the button to loading mode.
   *
   * @event lockUpdate
   * @property {boolean} value - The lock state value
   */
  (e: 'lockUpdate', value: boolean): void
  (e: 'tokenReceived', value: string | null): void
}>()

const LOCK_TIMEOUT = secondsToMilliseconds(7)

const { t } = useI18n()
const { isDebug, token, load, render, execute } = useRecaptcha()
const formErrors = useFormErrors()

const isLocked = ref(false)
watch(isLocked, () => {
  emits('lockUpdate', isLocked.value)
})

let lockTimeout: number | null = null

function lockValidation(): void {
  lockTimeout && window.clearTimeout(lockTimeout)
  isLocked.value = true
}

function scheduleUnlockValidation(): void {
  lockTimeout && window.clearTimeout(lockTimeout)
  lockTimeout = window.setTimeout(unlockValidation, LOCK_TIMEOUT)
}

function unlockValidation(): void {
  lockTimeout && window.clearTimeout(lockTimeout)
  isLocked.value = false
}

async function rule(): Promise<string | true> {
  // in addition to validation, the function is called during field initialization
  if (!meta.pending) {
    return true
  }

  // validate after all synchronous checks are completed
  await new Promise((resolve) => {
    setTimeout(resolve, 0)
  })

  const hasOtherFormErrors = Object.keys(formErrors.value).some(
    (field) => field !== props.name
  )

  if (hasOtherFormErrors) {
    return true
  }

  if (isLocked.value) {
    return t('recaptcha.throttling_error')
  }

  lockValidation()

  try {
    await load()
    render()

    scheduleUnlockValidation()

    await execute()

    if (!token.value) {
      throw new Error('Empty reCAPTCHA token')
    }

    unlockValidation()

    emits('tokenReceived', token.value)
  } catch (error: any) {
    if (error instanceof RecaptchaReset) {
      // we do nothing, because a new check has already been launched
      return ''
    }

    unlockValidation()
    useLogError(error)
    return t('recaptcha.validation_error')
  }

  return true
}

const { errorMessage, value, meta } = useField(props.name, rule, {
  validateOnMount: false,
  validateOnValueUpdate: false,
  syncVModel: false,
  initialValue: null,
})

onMounted(async () => {
  try {
    await load()
    render()
  } catch (error: any) {
    useLogError(error)
  }
})
</script>

<style>
.grecaptcha-badge {
  visibility: hidden;
}
</style>
